import React, { useCallback, useMemo } from 'react';

import {
  Box,
  CircularProgress,
  IconButton,
  Switch,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';

import { Member } from 'src/Superadmin/index.types';
import { StyledFlex } from 'src/components/StyledFlex';
import { useNavigate } from 'react-router-dom';
import { StyledActionCell, stickyNameCellSx } from './styles';
import { EditSharp } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { CountryBranchGroup } from './Row';
import { useOrganizationMember } from './hooks';
import { CountryRollup } from 'src/Superadmin/components/CountryGroupHeader';

interface Props {
  member: Member;
  // Pre-grouped, already-filtered branch columns from the table shell
  // (OptimizedUsersTable.tsx or the legacy UsersTable/index.tsx) -- the same
  // prop Row.tsx takes, and for the same reason: this row no longer fetches
  // its own branch list, so it can never disagree with the header above it
  // (previously a real risk -- this used its OWN useBranches() call, a
  // second, independent fetch from whatever built the header's columns).
  groups: CountryBranchGroup[];
  // specs/.../20260826-fe-country-group-folding.md: resolves a group's
  // collapse state, computed once in the table shell and shared with Row.tsx
  // and the header rows above (Goal 4) -- see Row.tsx's identical prop.
  isGroupOpen: (key: string) => boolean;
}
export default function SupplierRow(props: Props) {
  const { member, groups, isGroupOpen } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Switch handlers below are UNCHANGED (F-11): they still mutate both the
  // user's and the organization's branches and resync supplier surveys via
  // PUT .../branches/with-organization -- only the SOURCE of the branch
  // columns being switched changed, from useBranches() to this `groups` prop.
  const { updateUserBranches, updateUserAndOrganizationBranch } =
    useOrganizationMember(member.id);

  const organizationBranchSet = useMemo(
    () =>
      new Set(
        (member.organizations || []).flatMap(
          (org) => org.organization_branches || []
        )
      ),
    [member.organizations]
  );

  // Goal 4: Kraj/Oddział, plain-text columns derived from the org's own
  // branches (not the user's -- distinct from the switches below, which
  // reflect either). Drawn from the SAME `groups` the switches use, so once
  // a country filter narrows `groups`, these two columns narrow with it too
  // -- consistent with the rest of the filtered view rather than a stray
  // unfiltered summary. D-06: a multi-country org lists every country/branch
  // it has, comma-joined; guardrail 1 keeps this plain text, no chips.
  const orgBranches = useMemo(
    () =>
      groups
        .flatMap((group) => group.branches)
        .filter((branch) => organizationBranchSet.has(branch.name)),
    [groups, organizationBranchSet]
  );
  const orgCountryText = useMemo(() => {
    const names = Array.from(
      new Set(
        orgBranches
          .map((branch) => branch.country_name || branch.country_code)
          .filter((name): name is string => Boolean(name))
      )
    );
    return names.length > 0 ? names.join(', ') : 'N/A';
  }, [orgBranches]);
  const orgBranchText = useMemo(() => {
    const labels = orgBranches.map(
      (branch) => branch.description || branch.name
    );
    return labels.length > 0 ? labels.join(', ') : 'N/A';
  }, [orgBranches]);

  const fullName =
    member.first_name || member.last_name
      ? `${member.first_name} ${member.last_name}`
      : member.email;

  const updateBranch = useCallback(
    (branch: string, value: boolean) => {
      const supplierOrgId = member.organizations?.[0]?.organization_id;
      if (supplierOrgId) {
        updateUserAndOrganizationBranch.mutate({
          organizationId: supplierOrgId,
          branch,
          value,
        });
      } else {
        // Fallback if no organization is present
        updateUserBranches.mutate({ branch, value });
      }
    },
    [member.organizations, updateUserAndOrganizationBranch, updateUserBranches]
  );

  return (
    <TableRow sx={{ p: 0 }}>
      <TableCell
        sx={{
          ...stickyNameCellSx,
          verticalAlign: 'top',
          minWidth: '400px',
          p: '8px',
        }}
      >
        <StyledFlex sx={{ alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6">{fullName}</Typography>
            <Typography variant="captionText" component="div">
              {member.email}
            </Typography>
          </Box>
          <IconButton
            onClick={() => navigate(`/superadmin/users/${member.id}`)}
          >
            <EditSharp sx={{ color: '#6E6E6E' }} />
          </IconButton>
        </StyledFlex>
      </TableCell>
      <TableCell>
        {member.organizations?.[0]?.organization_name || 'N/A'}
      </TableCell>
      <TableCell>{orgCountryText}</TableCell>
      <TableCell>{orgBranchText}</TableCell>
      {groups.map((group) => {
        if (isGroupOpen(group.key)) {
          return group.branches.map((branch) => (
            <StyledActionCell key={branch.name}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Switch
                  checked={
                    member.branches.includes(branch.name) ||
                    organizationBranchSet.has(branch.name)
                  }
                  onChange={(e) => updateBranch(branch.name, e.target.checked)}
                  disabled={
                    updateUserBranches.isLoading ||
                    updateUserAndOrganizationBranch.isLoading
                  }
                  aria-label={`Toggle ${
                    branch.description || branch.name
                  } access for ${fullName}`}
                  sx={{
                    opacity: updateUserBranches.isLoading ? 0.6 : 1,
                    transition: 'opacity 0.2s ease-in-out',
                  }}
                />
                {(updateUserBranches.isLoading ||
                  updateUserAndOrganizationBranch.isLoading) && (
                  <CircularProgress size={16} sx={{ color: 'primary.main' }} />
                )}
              </Box>
            </StyledActionCell>
          ));
        }

        // Goal 2/BR-2/BR-3/EC-2/EC-7: collapsed -- roll-up over the SAME
        // "on" definition the open switches above use (either the user's
        // own branch grant or the supplier organization's own branch),
        // never a blank cell.
        const on = group.branches.filter(
          (branch) =>
            member.branches.includes(branch.name) ||
            organizationBranchSet.has(branch.name)
        ).length;
        return (
          <StyledActionCell key={group.key}>
            <CountryRollup
              on={on}
              total={group.branches.length}
              ariaLabel={String(
                t('superadmin.settings.countryFolding.rollupAriaLabel', {
                  on,
                  total: group.branches.length,
                })
              )}
            />
          </StyledActionCell>
        );
      })}
    </TableRow>
  );
}
